import {
    Field,
    Option,
    Button,
    type OptionOnSelectData,
    type SelectionEvents,
    Popover,
    PopoverSurface,
    Combobox,
    makeStyles,
    tokens,
    Input,
    PositioningImperativeRef,
} from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import { ICompany } from "../IDB";


const useStyles = makeStyles({
    popover: {
        padding: tokens.spacingHorizontalM,
        display: 'grid',
        gridTemplateAreas: "'input input' 'okButton cancelButton'",
        gap: tokens.spacingHorizontalL
    },
    input: {
        gridArea: 'input',
    },
    okButton: {
        gridArea: 'okButton',
    },
    cancelButton: {
        gridArea: 'cancelButton',
    },
    hint: {
        color: tokens.colorBrandForegroundInverted,
        fontSize: "12px",
        padding: '0px',
        display: 'contents',
        position: 'relative',
    }
});

interface IDropdownWithCreateButtonProps {
    defaultOption: { key: string; text: string };
    onCreateNew: React.Dispatch<React.SetStateAction<ICompany | undefined>>;
    onOptionSelect: (event: SelectionEvents, data: OptionOnSelectData) => void;
    options: { key: string; text: string }[];
    width: string | number;
}

export const DropdownWithCreateButton = (props: IDropdownWithCreateButtonProps) => {
    const styles = useStyles();
    const [options, setOptions] = useState(props.options);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const InputRef = useRef<HTMLInputElement>(null);
    const [inputError, setInputError] = useState<string>("");
    const dropdownRef = React.useRef<HTMLInputElement>(null);
    const positioningRef = React.useRef<PositioningImperativeRef>(null);
    const [selectedOptions, setSelectedOptions] = React.useState<string[]>([props.defaultOption.key]);
    const [value, setValue] = React.useState(props.defaultOption.text);

    const ReactOptions = options.sort((a, b) => a.text.localeCompare(b.text)).map(option => (
        <Option key={option.key} value={option.key}>
            {option.text}
        </Option>
    ));

    const handleCreateNew = () => {
        setIsPopoverOpen(true);
    };

    const handleNewOptionSubmit = () => {
        const newValue = InputRef.current?.value.trim();
        if (!newValue || newValue === "") {
            setInputError("Option name cannot be empty.");
            return;
        }
        if (props.options.some((o) => o.text === newValue)) {
            setInputError("An option with that name already exists!");
            return;
        }
        setOptions([...options, { key: '-1', text: `(NEW) ${newValue}` }]);
        setSelectedOptions(["-1"]);
        setValue(`(NEW) ${newValue}`);
        props.onCreateNew({ id: -1, name: newValue });
        setIsPopoverOpen(false);
    };

    const onOptionSelect = (event: SelectionEvents, data: OptionOnSelectData) => {
        if (data && data.optionValue && data.optionText) {
            setSelectedOptions(data.selectedOptions);
            setValue(data.optionText ?? "")
            props.onOptionSelect(event, data);
        }
    };

    const onInput = (ev: React.ChangeEvent<HTMLInputElement>) => {
        setValue(ev.target.value);
    };

    useEffect(() => {
        if (dropdownRef.current) {
            positioningRef.current?.setTarget(dropdownRef.current);
        }
    }, [dropdownRef, positioningRef]);

    return (
        <Field hint={<Button className={styles.hint} appearance="transparent" onClick={handleCreateNew}>Create New</Button>}>
            <Popover open={isPopoverOpen} withArrow positioning={{ positioningRef }}>
                <Combobox placeholder="Select an option" onInput={onInput} onOptionSelect={onOptionSelect} ref={dropdownRef} value={value} selectedOptions={selectedOptions}>
                    {ReactOptions}
                </Combobox>
                <PopoverSurface style={{ width: props.width }}>
                    <div className={styles.popover}>
                        <Field className={styles.input} label="Name" required validationMessage={inputError ? inputError : null} validationState={inputError ? 'error' : 'none'}>
                            <Input ref={InputRef} />
                        </Field>
                        <Button className={styles.okButton} appearance="primary" shape="square" onClick={handleNewOptionSubmit}>OK</Button>
                        <Button className={styles.cancelButton} appearance="outline" shape="square" onClick={() => setIsPopoverOpen(false)}>Cancel</Button>
                    </div>
                </PopoverSurface>
            </Popover>
        </Field>
    );
};